import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
  clearRetryState,
  setRetryingErrorId,
} from '../state/aiSidekickChatSlice';

/**
 * Coordinates the active retry id with the Redux retry surface.
 * @returns Stable controls for reserving, activating, and clearing a retry
 */
export const useConversationRetryState = () => {
  const dispatch = useDispatch();
  const activeRetryErrorIdRef = useRef<string | null>(null);

  const reserveRetry = useCallback((errorId: string): boolean => {
    if (activeRetryErrorIdRef.current !== null) {
      return false;
    }
    activeRetryErrorIdRef.current = errorId;
    return true;
  }, []);

  const cancelRetryReservation = useCallback((errorId: string): void => {
    if (activeRetryErrorIdRef.current === errorId) {
      activeRetryErrorIdRef.current = null;
    }
  }, []);

  const activateRetry = useCallback(
    (errorId: string): void => {
      if (activeRetryErrorIdRef.current !== errorId) {
        return;
      }
      dispatch(setRetryingErrorId(errorId));
    },
    [dispatch]
  );

  const clearActiveRetryState = useCallback((): void => {
    activeRetryErrorIdRef.current = null;
    dispatch(clearRetryState());
  }, [dispatch]);

  return {
    activateRetry,
    cancelRetryReservation,
    clearActiveRetryState,
    reserveRetry,
  };
};
